import { useEffect, useState } from 'react';

import { ActorIcon, type ActorIconKind } from '@/components/ActorIcon';

/**
 * Discoverable help legend.
 *
 * A small "?" chip in the header opens a dismissable side panel that
 * explains the iconography:
 *
 *   * the four floors and what they mean for trading;
 *   * the agent role icons that float above each character in the
 *     pod-room view (QA / SA / FA / Researcher / RM / EA);
 *   * the mood-tinted ring colours and what each implies.
 *
 * Pure DOM/CSS so it doesn't fight the Pixi GL context; lives at
 * App level so it overlays everything else.
 *
 * Auto-shows on the very first visit per browser (localStorage flag),
 * dismissable forever after.  Esc closes it.
 */

const STORAGE_KEY = 'hedgefundweb.helpLegendShown';

interface LegendItem {
  label: string;
  description: string;
  swatch?: string;
  swatchKind?: 'ring' | 'dot' | 'icon';
  icon?: ActorIconKind;
}

const FLOOR_ITEMS: LegendItem[] = [
  {
    label: 'Canary Lab',
    description: 'Sandbox pods that test new policy versions in shadow mode.',
    swatch: '#1f3a5f',
    swatchKind: 'dot',
  },
  {
    label: 'Active Offices',
    description: 'Pods trading at full size. Most of the action happens here.',
    swatch: '#21262d',
    swatchKind: 'dot',
  },
  {
    label: 'Reduced Floor',
    description: 'Pods on a 30–50% haircut after soft risk breaches.',
    swatch: '#8b5a2b',
    swatchKind: 'dot',
  },
  {
    label: 'Frozen Basement',
    description: 'Pods suspended by the Risk Officer after a hard breach.',
    swatch: '#58a6ff',
    swatchKind: 'dot',
  },
];

const RISK_ITEMS: LegendItem[] = [
  { label: 'Active', description: 'Normal trading, no risk haircut.', swatch: '#2ea043', swatchKind: 'ring' },
  { label: 'Watch', description: 'Soft signal triggered, still trading.', swatch: '#d29922', swatchKind: 'ring' },
  { label: 'Reduced', description: 'Trading at a reduced size (haircut applied).', swatch: '#db6d28', swatchKind: 'ring' },
  { label: 'Frozen', description: 'Suspended after a hard breach.', swatch: '#58a6ff', swatchKind: 'ring' },
];

const ROLE_ITEMS: LegendItem[] = [
  { label: 'Quant', description: 'Stat-arb signal from price/volume features.', icon: 'qa' },
  { label: 'Sentiment', description: 'News + social sentiment classifier.', icon: 'sa' },
  { label: 'Fundamental', description: 'Earnings, ratios, balance-sheet reads.', icon: 'fa' },
  { label: 'Researcher', description: 'Deep research over filings and reports.', icon: 'researcher' },
  { label: 'Risk Manager', description: 'In-pod risk pre-check before the PM decides.', icon: 'rm' },
  { label: 'Execution', description: 'Translates the PM decision into orders.', icon: 'ea' },
  { label: 'Portfolio Manager', description: 'Weighs the six signals and decides.', icon: 'pm' },
];

const MOOD_ITEMS: LegendItem[] = [
  { label: 'Thinking', description: 'Agent is running an LLM call.', swatch: '#58a6ff', swatchKind: 'ring' },
  { label: 'Confident', description: 'Signal landed with high confidence.', swatch: '#2ea043', swatchKind: 'ring' },
  { label: 'Worried', description: 'Soft warning or low conviction.', swatch: '#d29922', swatchKind: 'ring' },
  { label: 'Angry', description: 'Risk Officer disapproval or hard breach.', swatch: '#f85149', swatchKind: 'ring' },
  { label: 'Celebrating', description: 'Friday party winner.', swatch: '#ffd33d', swatchKind: 'ring' },
];

export function HelpLegend() {
  const [open, setOpen] = useState(false);

  // First-visit auto-open.
  useEffect(() => {
    try {
      const shown = window.localStorage.getItem(STORAGE_KEY);
      if (!shown) {
        setOpen(true);
        window.localStorage.setItem(STORAGE_KEY, '1');
      }
    } catch {
      /* localStorage disabled */
    }
  }, []);

  // Esc to close.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="What's on this dashboard?"
        aria-expanded={open}
        title="Открыть легенду дашборда: что значат комнаты, цвета, иконки, бейджи, типы событий. Используй когда не понимаешь конкретный элемент UI."
        style={{
          background: open ? '#1f6feb' : 'transparent',
          color: open ? 'white' : '#8b949e',
          border: '1px solid #30363d',
          borderRadius: 6,
          padding: '2px 8px',
          fontSize: 11,
          fontWeight: 700,
          cursor: 'pointer',
          letterSpacing: 0.4,
          minHeight: 24,
        }}
      >
        ? help
      </button>
      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Dashboard legend"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 90,
            background: 'rgba(5, 8, 16, 0.7)',
            display: 'flex',
            justifyContent: 'flex-end',
            backdropFilter: 'blur(2px)',
          }}
          onClick={() => setOpen(false)}
        >
          <aside
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(440px, 100%)',
              height: '100%',
              background: '#0d1117',
              borderLeft: '1px solid #21262d',
              overflowY: 'auto',
              padding: '20px 24px',
              color: '#e6edf3',
              boxShadow: '-12px 0 24px rgba(0,0,0,0.5)',
            }}
          >
            <header
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                marginBottom: 14,
              }}
            >
              <h2 style={{ margin: 0, fontSize: 16 }}>What's on this dashboard?</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close help"
                style={{
                  background: 'transparent',
                  color: '#8b949e',
                  border: 'none',
                  fontSize: 18,
                  cursor: 'pointer',
                  padding: '0 4px',
                }}
              >
                ×
              </button>
            </header>
            <p style={{ fontSize: 12, color: '#8b949e', marginTop: 0 }}>
              The dashboard shows a hedge fund as a four-storey building.
              Each <strong>pod</strong> is an autonomous trading team that lives on a
              floor according to its risk state.  Click any pod window to step inside.
            </p>

            <Section title="Floors">
              {FLOOR_ITEMS.map((i) => (
                <Row key={i.label} item={i} />
              ))}
            </Section>

            <Section title="Pod window border (risk state)">
              {RISK_ITEMS.map((i) => (
                <Row key={i.label} item={i} />
              ))}
            </Section>

            <Section title="Agent roles (inside a pod room)">
              {ROLE_ITEMS.map((i) => (
                <Row key={i.label} item={i} />
              ))}
            </Section>

            <Section title="Mood ring (around each character's head)">
              {MOOD_ITEMS.map((i) => (
                <Row key={i.label} item={i} />
              ))}
            </Section>

            <Section title="Live demo">
              <p style={{ fontSize: 12, color: '#8b949e', margin: '6px 0 8px' }}>
                The market is normally closed on weekends.  The{' '}
                <strong>TRY DEMO</strong> chip in the header (and the{' '}
                <code style={{ color: '#e6edf3' }}>?demo=1</code> URL param)
                replays a scripted event stream so you can see every animation
                without waiting for trading hours.
              </p>
            </Section>
          </aside>
        </div>
      ) : null}
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 18 }}>
      <h3
        style={{
          margin: 0,
          fontSize: 11,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: '#8b949e',
          marginBottom: 8,
        }}
      >
        {title}
      </h3>
      {children}
    </section>
  );
}

function Row({ item }: { item: LegendItem }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '38px 1fr',
        alignItems: 'flex-start',
        gap: 12,
        padding: '7px 0',
        borderTop: '1px solid #161b22',
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: '#f2e8c9',
          border:
            item.swatchKind === 'ring'
              ? `3px solid ${item.swatch}`
              : '1px solid #21262d',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {item.swatchKind === 'dot' ? (
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: item.swatch,
            }}
          />
        ) : null}
        {item.icon ? <ActorIcon kind={item.icon} size={14} /> : null}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{item.label}</div>
        <div style={{ fontSize: 12, color: '#8b949e' }}>{item.description}</div>
      </div>
    </div>
  );
}

